import { Dialog } from "@kobalte/core/dialog";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { type Component, For, Show } from "solid-js";
import { useI18n } from "~/i18n/context";

interface FileUploadErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  filenames?: string[];
  /** Asset IDs for already-in-flight uploads (in-queue case). When present (length > 0), a "Use in Chat" CTA renders that calls onUseInChat. */
  assetIds?: string[];
  onUseInChat?: (assetIds: string[]) => void;
}

/**
 * General error modal component for file upload errors
 * Can be used for validation errors, duplicate files, and other upload issues
 */
export const FileUploadErrorModal: Component<FileUploadErrorModalProps> = (props) => {
  const { t } = useI18n();

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-120 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-120 flex items-center justify-center">
              <Dialog.Content class="relative max-h-[80vh] w-screen max-w-md overflow-y-auto rounded-xl bg-panel p-6 text-primary-text">
                <div class="mb-6">
                  <h3 class="mb-4 text-lg font-medium">{props.title}</h3>
                  <div class="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/20">
                    <div class="flex items-start gap-2">
                      <span class="shrink-0 text-lg">🛑</span>
                      <div class="flex-1">
                        <p class="text-sm text-red-800 dark:text-red-200">{props.message}</p>
                        {props.filenames && props.filenames.length > 0 && (
                          <ul class="mt-2 list-inside list-disc text-xs text-red-700 dark:text-red-300">
                            <For each={props.filenames}>{(filename) => <li>{filename}</li>}</For>
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="flex items-center justify-end gap-3">
                  <Show
                    when={props.onUseInChat && (props.assetIds?.length ?? 0) > 0}
                    fallback={
                      <StButton onClick={props.onClose} class="bg-brand px-4 py-2 text-on-brand hover:bg-brand-hover">
                        {t("common.close")}
                      </StButton>
                    }
                  >
                    <StButton onClick={props.onClose} class="px-4 py-2 text-secondary-text hover:text-primary-text">
                      {t("common.close")}
                    </StButton>
                    <StButton
                      onClick={() => {
                        props.onUseInChat?.(props.assetIds ?? []);
                        props.onClose();
                      }}
                      class="bg-brand px-4 py-2 text-on-brand hover:bg-brand-hover"
                    >
                      {t("error_duplicate_use_in_chat")}
                    </StButton>
                  </Show>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
